// src/lib/plans.js
// Single source of truth for subscription plans, pricing, and feature gates.
// Monthly prices in Ghana Cedis (GHS) per user per month.
// Annual prices in Ghana Cedis (GHS) per user per YEAR, computed as
// 12 × monthly × (1 - ANNUAL_DISCOUNT_PERCENT/100).
//
// FOUNDING-MEMBER PRICING — 2026 entry.
// These prices are the "Year-1 entry" rates designed to drive adoption
// during the launch window.
//
// CURRENT RATES:
//     Basic monthly: GH₵200    · annual eff: GH₵160/mo · 5-seat cap
//     Pro monthly:   GH₵1,000  · annual eff: GH₵800/mo · 10-seat cap
//     Enterprise:    Custom    · unlimited seats (firms > 10 lawyers)
//
// Pro is positioned as a small-to-mid firm tier — capped at 10 seats so
// growing firms have a clear next step (Enterprise = custom contract +
// dedicated success management). The 10-seat cap is enforced server-
// side by the migration-141 trigger on firms.seat_limit (set by the
// Paystack webhook on subscription.create from PLAN_CAPACITY in
// supabase/functions/paystack-webhook/index.ts).
//
// Promise to early adopters: annual signups during the founding-member
// window get their rate locked for FOUNDING_MEMBER_LOCK_MONTHS months
// (currently 12 — one full annual billing cycle). After that window
// closes new sign-ups see steady-state rates and existing customers get
// at least 60 days notice before any change.
//
// Storage caps:
//   Basic:      20 GB     (covers solo + 2-3 person practices with headroom
//                          for scanned documents + photo evidence)
//   Pro:        200 GB    (covers 5-15 person firms even with heavy AV /
//                          deposition recordings)
//   Enterprise: NULL      (unlimited)
//
// Generous-but-finite caps: ~95% of customers will never approach these
// limits, but the numbers are concrete enough to advertise as a benefit
// (vs the vague "unlimited" competitors use). The existing storage
// add-on flow (BuyStorageModal + add_firm_storage_addon RPC) is the
// escape valve for the 5% who legitimately need more.
//
// Unit economics: Supabase file storage is ~$0.021/GB/month above the
// 100 GB included with Pro plan. Worst-case 200 GB Pro customer costs
// ~$4.20/month in raw storage; realistic average per Pro customer is
// closer to $1. That's under 1% of revenue at the founding-member
// rates, so the cap is more about positioning than cost control.
//
// MUST stay in sync with PLAN_CAPACITY in
// supabase/functions/paystack-webhook/index.ts — the webhook is what
// actually sets `firms.storage_limit_mb` on subscription.create.

export const ANNUAL_DISCOUNT_PERCENT = 20;

// "Locked for X months" promise to founding-member annual subscribers.
// Surfaced in the SubscriptionSuspendedPage UI, the marketing PricingPage,
// and CHANGELOG v1.0.22. Originally 24 months (matched a typical annual
// renewal cycle); shortened to 12 months in v1.0.33 to align with one
// full annual billing cycle — making the promise unambiguously "your
// first year is locked in at this rate."
export const FOUNDING_MEMBER_LOCK_MONTHS = 12;

export const PLANS = {
  trial: {
    key:             'trial',
    name:            'Pro',          // trial = pro in all display contexts
    priceGhs:        0,
    priceGhsAnnual:  0,
    maxUsers:        null,           // same as pro — unlimited during trial
    storageGb:       200,            // same as pro during trial
    badge:           'Pro',
    badgeColor:      'bg-indigo-100 text-indigo-700',
    cta:             'Upgrade to Pro',
    description:     'Full-featured practice management for growing firms',
    popular:         true,
  },
  basic: {
    key:             'basic',
    name:            'Basic',
    priceGhs:        200,            // founding-member rate (was 150)
    // 200 × 12 × 0.80 = 1920.
    priceGhsAnnual:  1920,
    maxUsers:        null,
    storageGb:       20,
    badge:           'Basic',
    badgeColor:      'bg-blue-100 text-blue-700',
    cta:             'Upgrade to Basic',
    description:     'Everything you need to run a lean solo practice',
  },
  pro: {
    key:             'pro',
    name:            'Pro',
    priceGhs:        1000,           // founding-member rate (was 400)
    // 1000 × 12 × 0.80 = 9600.
    priceGhsAnnual:  9600,
    maxUsers:        null,
    storageGb:       200,
    badge:           'Pro',
    badgeColor:      'bg-indigo-100 text-indigo-700',
    cta:             'Upgrade to Pro',
    description:     'Full-featured practice management for small-to-mid firms (up to 10 lawyers)',
    popular:         true,
  },
  enterprise: {
    key:             'enterprise',
    name:            'Enterprise',
    priceGhs:        null,  // custom pricing
    priceGhsAnnual:  null,  // custom pricing
    maxUsers:        null,
    storageGb:       null,  // unlimited
    badge:           'Enterprise',
    badgeColor:      'bg-purple-100 text-purple-700',
    cta:             'Contact us',
    description:     'Custom setup for large firms and chambers',
  },
};

// Plan upgrade order (for comparison).
const PLAN_ORDER = ['trial', 'basic', 'pro', 'enterprise'];

// ── Feature flags per plan ───────────────────────────────────────────────────
// A feature is available on a plan if it's listed under that plan OR any
// higher-tier plan (plans inherit all features of plans below them).
//
// Each key maps to the MINIMUM plan required.
export const FEATURE_PLAN = {
  // ── TRIAL / BASIC ──────────────────────────────────────────────────────────
  'cases':           'trial',
  'contacts':        'trial',
  'tasks':           'trial',
  'calendar':        'trial',
  'deadlines':       'trial',
  'time_tracking':   'trial',
  'documents':       'trial',
  'invoicing.basic': 'trial',
  'expenses':        'trial',
  'dashboard':       'trial',

  // ── PRO ───────────────────────────────────────────────────────────────────
  'trust':           'pro',
  'reports':         'pro',
  'rbac':            'pro',
  'roles':           'pro',
  'client_portal':   'pro',
  'conflict_check':  'pro',
  'intake':          'pro',
  'employees':       'pro',
  'billing_rates':   'pro',
  'invoicing.pro':   'pro',  // recurring invoices, payment links
  'custom_roles':    'pro',
  'storage.200gb':    'pro',

  // ── ENTERPRISE ────────────────────────────────────────────────────────────
  'ai.case_search':     'enterprise',
  'ai.doc_summary':     'enterprise',
  'ai.contract_review': 'enterprise',
  'ai.deadline_alerts': 'enterprise',
  'ai.billing':         'enterprise',
  'storage.unlimited':  'enterprise',
  'support.priority':   'enterprise',
  'support.dedicated':  'enterprise',
  'custom_integrations':'enterprise',
};

// ── Human-readable feature labels for the upgrade modal ─────────────────────
export const FEATURE_LABELS = {
  // Basic
  'cases':           'Case management',
  'contacts':        'Client & contact management',
  'tasks':           'Tasks & to-dos',
  'calendar':        'Calendar & scheduling',
  'deadlines':       'Deadlines & court dates',
  'time_tracking':   'Time tracking',
  'documents':       'Document management (20 GB)',
  'invoicing.basic': 'Invoicing & payments',
  'expenses':        'Expense tracking',
  'dashboard':       'Dashboard & overview',
  // Pro
  'trust':           'Client Account management',
  'reports':         'Advanced reports & analytics',
  'rbac':            'Role-based access control',
  'client_portal':   'Client portal',
  'conflict_check':  'Conflict of interest checker',
  'intake':          'Online intake forms',
  'employees':       'Employee & HR management',
  'billing_rates':   'Custom billing rates per matter',
  'invoicing.pro':   'Recurring invoices & payment links',
  'custom_roles':    'Custom roles & permissions',
  'storage.200gb':    'Document storage (200 GB)',
  // Enterprise
  'ai.case_search':      'AI case search & research',
  'ai.doc_summary':      'AI document summarisation',
  'ai.contract_review':  'AI contract review',
  'ai.deadline_alerts':  'Smart deadline alerts (AI)',
  'ai.billing':          'AI billing suggestions',
  'storage.unlimited':   'Unlimited storage',
  'support.priority':    'Priority support',
  'support.dedicated':   'Dedicated account manager',
  'custom_integrations': 'Custom integrations & API access',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Returns true if the given plan includes the feature */
export function planIncludes(planKey, featureKey) {
  const required = FEATURE_PLAN[featureKey];
  if (!required) return true; // unknown feature — allow by default
  // 'trial' is treated as 'pro' — trial plan = pro plan feature-wise
  const effective = planKey === 'trial' ? 'pro' : planKey;
  return PLAN_ORDER.indexOf(effective) >= PLAN_ORDER.indexOf(required);
}

/** Returns the minimum plan name that includes a feature */
export function minPlanFor(featureKey) {
  const required = FEATURE_PLAN[featureKey];
  return PLANS[required] ?? PLANS.basic;
}

/** Features grouped by plan for the upgrade modal */
export function featuresForPlan(planKey) {
  return Object.entries(FEATURE_PLAN)
    .filter(([, min]) => min === planKey)
    .map(([key]) => ({ key, label: FEATURE_LABELS[key] ?? key }));
}

/** Ordered list of plans (excluding trial) for the pricing table */
export const PAID_PLANS = PLAN_ORDER.filter(k => k !== 'trial').map(k => PLANS[k]);

/** planIncludes compat: treat 'ai' as 'enterprise' for legacy references */
export function normalizePlanKey(key) {
  return key === 'ai' ? 'enterprise' : key;
}
